import { FAMILY_TZ } from './clock'

/*
 * The auto-draft's clock.
 *
 * This decides, with nobody watching, whether to publish a board to the whole
 * family. Every way it can be wrong is silent: a window off by a day boards
 * the wrong games, and a deadline computed against the wrong week publishes
 * over a commissioner who was still thinking about it.
 *
 * So all of it is pure, and all of it is tested against real calendar dates.
 */

const DAY = 86_400

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/**
 * The local day of week, 0 = Sunday.
 *
 * Asked of the family's zone, never of the server's. 2026-09-10T04:00Z is ten
 * at night on Wednesday in Denver; a UTC read calls it Thursday and the whole
 * window jumps a week.
 */
export function dayOfWeek(unixSeconds: number, tz: string = FAMILY_TZ): number {
  const s = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' })
    .format(new Date(unixSeconds * 1000))
  return DAYS.indexOf(s)
}

/** Local midnight on the day containing `unixSeconds`. */
function localMidnight(unixSeconds: number, tz: string): number {
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23',
  })
  const parts = Object.fromEntries(f.formatToParts(new Date(unixSeconds * 1000)).map((p) => [p.type, p.value]))
  const secondsIntoDay =
    Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second)
  return unixSeconds - secondsIntoDay
}

export interface Window {
  opensAt: number
  closesAt: number
}

/**
 * The weekend a board would cover: Thursday 00:00 local to the following
 * Tuesday 00:00.
 *
 * Wide enough at both ends to hold a Thursday night college game and a Monday
 * night NFL one, which is the whole span a single board has to reach across.
 *
 * From Thursday through Sunday it returns the weekend ALREADY UNDER WAY - a
 * commissioner publishing on the morning of must get today's games, not next
 * week's. From Monday, Tuesday or Wednesday it looks ahead to the next one.
 */
export function weekendWindow(unixSeconds: number, tz: string = FAMILY_TZ): Window {
  const dow = dayOfWeek(unixSeconds, tz)
  /*
   * Monday, Tuesday and Wednesday look FORWARD to the coming Thursday;
   * Thursday through Sunday stay on the weekend already under way.
   *
   * Monday is the case that matters. Treating it as the tail of the weekend
   * just finished - which is literally true, since the window runs to Tuesday -
   * made the deadline read as long past, and the auto-draft would have
   * published a board for a weekend that had already been played.
   */
  const opensAt =
    dow >= 1 && dow <= 3
      ? localMidnight(unixSeconds + (4 - dow) * DAY, tz)
      : localMidnight(unixSeconds - ((dow - 4 + 7) % 7) * DAY, tz)
  return { opensAt, closesAt: opensAt + 5 * DAY }
}

/**
 * Whether the deadline for the CURRENT weekend's board has passed.
 *
 * Measured against that weekend's own Thursday, not against an absolute
 * weekday test. Asking "is it later than Wednesday 6pm?" is true every Friday,
 * Saturday and Monday as well, and would publish a board five days early for a
 * weekend nobody has thought about yet.
 */
/*
 * NOT the auto-draft's path, and it must not become it again.
 *
 * This asks whether the deadline has passed for whichever weekend TODAY falls
 * in, which on a Saturday is the weekend being played. The auto-draft asked
 * exactly that, found Wednesday long gone, and filled a board with games that
 * had already kicked off. It now measures `deadlineFor` against the window it
 * is about to publish - see the note on that function.
 *
 * Kept because the question it answers is a real one and the tests describe the
 * daylight-saving edges precisely. Nothing in production calls it.
 */
export function pastDeadline(
  unixSeconds: number,
  tz: string = FAMILY_TZ,
  dow = 3,
  hour = 18,
): boolean {
  const { opensAt } = weekendWindow(unixSeconds, tz)
  return unixSeconds >= deadlineFor(opensAt, tz, dow, hour)
}

/**
 * The moment a given window's board is due, as a timestamp.
 *
 * Taken against the window being PUBLISHED rather than against whichever
 * weekend today falls in, and the difference is not academic: the auto-draft
 * asked weekendWindow which weekend it was - on a Saturday, the one being
 * played - then asked whether Wednesday had passed, which by Saturday it
 * certainly had. With no board in the table it filled one with games that had
 * already kicked off. Both halves now measure the same weekend.
 */
export function deadlineFor(
  opensAt: number,
  tz: string = FAMILY_TZ,
  dow = 3,
  hour = 18,
): number {
  // The deadline day sits before that Thursday: Wednesday is one day back.
  return localMidnight(opensAt - (4 - dow) * DAY, tz) + hour * 3600
}

/**
 * The window a NEW board should cover.
 *
 * `weekendWindow` answers "which weekend is this?", and from Thursday through
 * Sunday that is the weekend already being played. That is right for a
 * commissioner publishing on Thursday morning for that night, and wrong by
 * Saturday: half the games are over, and a board covering them is a board
 * nobody can pick.
 *
 * So a weekend that has been running for more than a day is passed over.
 * Thursday still offers itself all day, which is the case worth keeping - the
 * board goes up in the morning and the first kickoff is that evening.
 *
 * There is no input for this anywhere. The commissioner should not have to
 * know which ESPN week it is, or hold in mind that college week 1 spans ten
 * days while the NFL has not started; a football week is Thursday to Monday
 * and the app can work that out for itself.
 */
export function nextBoardWindow(unixSeconds: number, tz: string = FAMILY_TZ): Window {
  const w = weekendWindow(unixSeconds, tz)
  if (unixSeconds < w.opensAt + DAY) return w
  const opensAt = w.opensAt + 7 * DAY
  return { opensAt, closesAt: opensAt + 5 * DAY }
}
